import { isDefined, isNumber, isString } from 'lib/typeguards';
import { Merge } from 'lib/types';

export enum CampaignEffect {
    ADD_SEGMENT_USER = 'ADD_SEGMENT_USER',
    ADD_AWARD = 'ADD_AWARD',
    REMOVE_AWARD = 'REMOVE_AWARD'
}

export interface SegmentAddUserEffect {
    type: CampaignEffect.ADD_SEGMENT_USER;
    data: {
        segmentId: string;
    };
}

export function isSegmentAddUserEffect(effect: any): effect is SegmentAddUserEffect {
    return (
        isDefined(effect) &&
        effect.type === CampaignEffect.ADD_SEGMENT_USER &&
        isDefined(effect.data) &&
        isString(effect.data.segmentId)
    );
}

export interface LoyaltyAddAwardEffectBase {
    type: CampaignEffect.ADD_AWARD;
    data: {
        perkId: string;
    };
}

export function isLoyaltyAddAwardEffectBase(effect: any): effect is LoyaltyAddAwardEffectBase {
    return (
        isDefined(effect) &&
        effect.type === CampaignEffect.ADD_AWARD &&
        isDefined(effect.data) &&
        isString(effect.data.perkId)
    );
}

export type LoyaltyAddAwardEffectStampCard = Merge<
    LoyaltyAddAwardEffectBase,
    {
        data: Merge<
            LoyaltyAddAwardEffectBase['data'],
            {
                type: 'STAMP_CARD';
                points: number;
            }
        >;
    }
>;

export function isLoyaltyAddAwardEffectStampCard(effect: any): effect is LoyaltyAddAwardEffectStampCard {
    return (
        isDefined(effect) &&
        isDefined(effect.data) &&
        effect.data.type === 'STAMP_CARD' &&
        isNumber(effect.data.points) &&
        isLoyaltyAddAwardEffectBase(effect)
    );
}

export type LoyaltyAddAwardEffectPseudoCurrency = Merge<
    LoyaltyAddAwardEffectBase,
    {
        data: Merge<
            LoyaltyAddAwardEffectBase['data'],
            {
                type: 'PSEUDO_CURRENCY';
                step: number;
                redemption: number;
            }
        >;
    }
>;

export function isLoyaltyAddAwardEffectPseudoCurrency(
    effect: any
): effect is LoyaltyAddAwardEffectPseudoCurrency {
    return (
        isDefined(effect) &&
        isDefined(effect.data) &&
        effect.data.type === 'PSEUDO_CURRENCY' &&
        isNumber(effect.data.step) &&
        isNumber(effect.data.redemption) &&
        isLoyaltyAddAwardEffectBase(effect)
    );
}

export type LoyaltyAddAwardEffect =
    | LoyaltyAddAwardEffectBase
    | LoyaltyAddAwardEffectStampCard
    | LoyaltyAddAwardEffectPseudoCurrency;

export function isLoyaltyAddAwardEffect(effect: any): effect is LoyaltyAddAwardEffect {
    return (
        isLoyaltyAddAwardEffectBase(effect) ||
        isLoyaltyAddAwardEffectStampCard(effect) ||
        isLoyaltyAddAwardEffectPseudoCurrency(effect)
    );
}

export interface LoyaltyRemoveAwardEffect {
    type: CampaignEffect.REMOVE_AWARD;
    data: {
        perkId: string;
    };
}

export function isLoyaltyRemoveAwardEffect(effect: any): effect is LoyaltyRemoveAwardEffect {
    return (
        isDefined(effect) &&
        effect.type === CampaignEffect.REMOVE_AWARD &&
        isDefined(effect.data) &&
        isString(effect.data.perkId)
    );
}

export type CampaignTriggerEffect = LoyaltyAddAwardEffect | LoyaltyRemoveAwardEffect | SegmentAddUserEffect;

export function isCampaignTriggerEffect(effect: any): effect is CampaignTriggerEffect {
    return (
        isDefined(effect) &&
        (isLoyaltyAddAwardEffect(effect) ||
            isLoyaltyRemoveAwardEffect(effect) ||
            isSegmentAddUserEffect(effect))
    );
}

export const campaignTriggerEffectLabel: Record<CampaignEffect, string> = {
    ADD_SEGMENT_USER: 'Add user to an audience',
    ADD_AWARD: 'Grant an award',
    REMOVE_AWARD: 'Remove an award'
};
